import { useMemo, useState, type ReactNode } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Analytics01Icon,
  ArrowUpRight01Icon,
  BookOpen01Icon,
  CheckmarkCircle02Icon,
  File01Icon,
  HelpCircleIcon,
  Message01Icon,
  PlayCircleIcon,
} from '@hugeicons/core-free-icons'
import { PageShell, PageHeader, EmptyState } from '@/components/shared/layout'
import { Button } from '@/components/ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { showIntercom } from '@/lib/utils/intercom'
import { FAQ_CATEGORIES, GUIDE_LINKS, SETUP_LINKS, EXTERNAL_RESOURCES } from '@/data/supportResources'

// Paper "Resources & support": row glyphs follow each list's order.
const SETUP_ICONS = [PlayCircleIcon, CheckmarkCircle02Icon, Analytics01Icon, File01Icon]
const EXTERNAL_ICONS = [File01Icon, PlayCircleIcon, BookOpen01Icon, HelpCircleIcon]

function Section({ title, meta, children }: { title: string; meta?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold leading-[18px]">{title}</h2>
        {meta && <span className="text-xs leading-4 text-foreground/60">{meta}</span>}
      </div>
      {children}
    </section>
  )
}

function LinkRow({ label, description, url, icon }: { label: string; description: string; url: string; icon?: typeof File01Icon }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-3 px-[18px] py-3.5 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
    >
      {icon && (
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground/60">
          <HugeiconsIcon icon={icon} size={15} />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium leading-[18px]">{label}</span>
        <span className="truncate text-xs leading-4 text-foreground/60">{description}</span>
      </span>
      <HugeiconsIcon icon={ArrowUpRight01Icon} size={13} className="shrink-0 text-foreground/35 transition-colors group-hover:text-foreground" />
    </a>
  )
}

function LinkCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="surface-raised flex flex-col overflow-clip">
      <h3 className="px-[18px] pb-1.5 pt-4 text-sm font-semibold leading-[18px]">{title}</h3>
      <div className="flex flex-col [&>*+*]:border-t [&>*+*]:border-border">{children}</div>
    </section>
  )
}

export default function Resources() {
  const [faqCategory, setFaqCategory] = useState(FAQ_CATEGORIES[0]?.id ?? 'start')
  const activeFaq = useMemo(
    () => FAQ_CATEGORIES.find((c) => c.id === faqCategory) ?? FAQ_CATEGORIES[0],
    [faqCategory],
  )

  return (
    <PageShell className="flex flex-col gap-7">
      <PageHeader
        title="Resources & support"
        description="Docs, guides and live chat. We usually reply within minutes during business hours."
        className="mb-0"
      />

      <div className="well flex flex-col gap-2">
        {/* Both CTAs open Intercom: chat is the support inbox. */}
        <section className="surface-raised flex flex-col gap-4 px-[18px] py-4 sm:flex-row sm:items-center">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground/70">
            <HugeiconsIcon icon={Message01Icon} size={17} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <h2 className="text-sm font-semibold leading-[18px]">Still have questions?</h2>
            <p className="text-[13px] leading-5 text-foreground/60">
              Live chat is the fastest way to reach us. Scale and Enterprise get priority.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" onClick={showIntercom} className="h-[34px] rounded-lg border-input bg-background px-3 text-xs font-medium shadow-none">
              Message support
            </Button>
            <Button onClick={showIntercom} className="h-[34px] rounded-lg px-3 text-xs font-medium">
              Start chat
            </Button>
          </div>
        </section>
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <LinkCard title="Setup and fundamentals">
            {SETUP_LINKS.map((link, i) => (
              <LinkRow key={link.url + link.label} {...link} icon={SETUP_ICONS[i % SETUP_ICONS.length]} />
            ))}
          </LinkCard>
          <LinkCard title="Browse elsewhere">
            {EXTERNAL_RESOURCES.map((link, i) => (
              <LinkRow key={link.url} {...link} icon={EXTERNAL_ICONS[i % EXTERNAL_ICONS.length]} />
            ))}
          </LinkCard>
        </div>
      </div>

      <Section title="Guides" meta={`${GUIDE_LINKS.length} popular`}>
        <div className="well grid grid-cols-1 gap-2 sm:grid-cols-2">
          {GUIDE_LINKS.map((guide) => (
            <div key={guide.url} className="surface-raised overflow-clip">
              <LinkRow label={guide.label} description={guide.description} url={guide.url} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="FAQ" meta="From docs and support">
        <div className="well">
          <section className="surface-raised flex flex-col px-[18px] pb-1 pt-4">
            <div role="tablist" aria-label="FAQ topics" className="flex w-fit max-w-full overflow-x-auto rounded-lg bg-muted p-[3px] scrollbar-hide">
              {FAQ_CATEGORIES.map((c) => {
                const on = c.id === faqCategory
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setFaqCategory(c.id)}
                    className={
                      on
                        ? 'h-[26px] shrink-0 rounded-md bg-card px-2.5 text-xs font-medium leading-4 text-foreground shadow-[0_0_0_1px_rgb(9_9_11/0.05),0_1px_2px_rgb(0_0_0/0.06)] dark:bg-white/10 dark:shadow-none'
                        : 'h-[26px] shrink-0 rounded-md px-2.5 text-xs leading-4 text-foreground/60 transition-colors hover:text-foreground'
                    }
                  >
                    {c.label}
                  </button>
                )
              })}
            </div>
            {activeFaq && activeFaq.items.length > 0 ? (
              <Accordion type="single" collapsible className="mt-2 w-full">
                {activeFaq.items.map((item, i) => (
                  <AccordionItem key={item.q} value={`faq-${faqCategory}-${i}`} className="border-border last:border-b-0">
                    <AccordionTrigger className="py-3.5 text-left text-sm font-normal leading-5 hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[13px] leading-5 text-foreground/60">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <EmptyState
                icon={<HugeiconsIcon icon={HelpCircleIcon} size={20} />}
                title="No FAQs in this category"
                description="Try another topic or start a chat."
              />
            )}
          </section>
        </div>
      </Section>
    </PageShell>
  )
}
